import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type {
  ProjectEquipmentAssignment,
  ProjectEquipmentGroupWithAssignments,
  TemporaryEquipment,
} from "@/hooks/useProjectEquipment";
import type { EquipmentUpdate } from "@/hooks/useEquipment";
import { formatEquipmentName } from "@/lib/equipment-utils";
import { useDraftField } from "@/hooks/useDraftField";

interface CarnetAtaEquipmentTableProps {
  groups: ProjectEquipmentGroupWithAssignments[];
  onUpdateEquipment: (id: string, updates: EquipmentUpdate) => void;
  onUpdateTemporaryEquipment: (
    id: string,
    updates: Partial<
      Pick<
        TemporaryEquipment,
        | "country_of_origin"
        | "carnet_ata_description"
        | "carnet_ata_value"
        | "weight"
        | "carnet_ata_weight_unit"
      >
    >,
  ) => void;
}

type CarnetAtaAssignmentRow = {
  groupName: string;
  assignment: ProjectEquipmentAssignment;
};

function formatNumberInputValue(value: number | null | undefined) {
  return value ?? "";
}

function parseOptionalNumber(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function TextDraftInput({
  value,
  placeholder,
  disabled,
  onCommit,
}: {
  value: string | null | undefined;
  placeholder?: string;
  disabled?: boolean;
  onCommit: (value: string | null) => void;
}) {
  const draft = useDraftField<string>({
    value: value ?? "",
    toShared: (next) => next.trim(),
    onCommit: (next) => onCommit(next || null),
  });

  return (
    <Input
      value={draft.draft}
      placeholder={placeholder}
      disabled={disabled}
      onFocus={draft.start}
      onChange={(event) => draft.setDraft(event.target.value)}
      onBlur={() => void draft.commit()}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          void draft.commit();
        }
        if (event.key === "Escape") {
          event.preventDefault();
          draft.cancel();
        }
      }}
      className="h-8"
    />
  );
}

function NumberDraftInput({
  value,
  placeholder,
  disabled,
  onCommit,
}: {
  value: number | null | undefined;
  placeholder?: string;
  disabled?: boolean;
  onCommit: (value: number | null) => void;
}) {
  const draft = useDraftField<string, number | null>({
    value: value ?? null,
    fromShared: (next) => formatNumberInputValue(next).toString(),
    toShared: (next) => parseOptionalNumber(String(next)),
    onCommit,
  });

  return (
    <Input
      type="number"
      step="0.01"
      value={draft.draft}
      placeholder={placeholder}
      disabled={disabled}
      onFocus={draft.start}
      onChange={(event) => draft.setDraft(event.target.value)}
      onBlur={() => void draft.commit()}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          void draft.commit();
        }
        if (event.key === "Escape") {
          event.preventDefault();
          draft.cancel();
        }
      }}
      className="h-8"
    />
  );
}

function CarnetAtaTableRow({
  groupName,
  assignment,
  onUpdateEquipment,
  onUpdateTemporaryEquipment,
}: CarnetAtaAssignmentRow & {
  onUpdateEquipment: (id: string, updates: EquipmentUpdate) => void;
  onUpdateTemporaryEquipment: CarnetAtaEquipmentTableProps["onUpdateTemporaryEquipment"];
}) {
  const equipment = assignment.equipment;
  const temporaryEquipment = assignment.temporary_equipment;
  const equipmentName = equipment
    ? formatEquipmentName(equipment.name, equipment.manufacturer)
    : temporaryEquipment?.name || "";
  const ataValues = equipment ?? temporaryEquipment;
  const disabled = !ataValues;

  const updateEquipment = (updates: EquipmentUpdate) => {
    if (equipment) {
      onUpdateEquipment(equipment.id, updates);
      return;
    }
    if (temporaryEquipment) {
      onUpdateTemporaryEquipment(temporaryEquipment.id, updates);
    }
  };

  return (
    <TableRow>
      <TableCell className="w-28 px-2 py-1 text-xs text-muted-foreground">
        {groupName}
      </TableCell>
      <TableCell className="w-12 px-2 py-1 text-right tabular-nums">
        {assignment.amount}
      </TableCell>
      <TableCell className="min-w-44 px-2 py-1">
        <div className="truncate text-sm" title={equipmentName}>
          {equipmentName || "Select equipment..."}
        </div>
      </TableCell>
      <TableCell className="min-w-56 px-2 py-1">
        <TextDraftInput
          value={ataValues?.carnet_ata_description}
          placeholder={
            equipment
              ? [equipment.manufacturer, equipment.name]
                  .filter(Boolean)
                  .join(" ")
              : "Temporary equipment"
          }
          disabled={disabled}
          onCommit={(value) =>
            updateEquipment({ carnet_ata_description: value })
          }
        />
      </TableCell>
      <TableCell className="w-44 px-2 py-1">
        <TextDraftInput
          value={ataValues?.country_of_origin}
          placeholder="Country"
          disabled={disabled}
          onCommit={(value) => updateEquipment({ country_of_origin: value })}
        />
      </TableCell>
      <TableCell className="w-32 px-2 py-1">
        <NumberDraftInput
          value={ataValues?.carnet_ata_value}
          placeholder={
            equipment?.new_price != null ? String(equipment.new_price) : "Value"
          }
          disabled={disabled}
          onCommit={(value) => updateEquipment({ carnet_ata_value: value })}
        />
      </TableCell>
      <TableCell className="w-28 px-2 py-1">
        <NumberDraftInput
          value={ataValues?.weight}
          placeholder="Weight"
          disabled={disabled}
          onCommit={(value) => updateEquipment({ weight: value })}
        />
      </TableCell>
      <TableCell className="w-24 px-2 py-1">
        <TextDraftInput
          value={ataValues?.carnet_ata_weight_unit ?? "kg"}
          placeholder="kg"
          disabled={disabled}
          onCommit={(value) =>
            updateEquipment({ carnet_ata_weight_unit: value || "kg" })
          }
        />
      </TableCell>
    </TableRow>
  );
}

export function CarnetAtaEquipmentTable({
  groups,
  onUpdateEquipment,
  onUpdateTemporaryEquipment,
}: CarnetAtaEquipmentTableProps) {
  const rows = groups.flatMap((group) =>
    group.assignments.map((assignment) => ({
      groupName: group.name,
      assignment,
    })),
  );

  if (rows.length === 0) {
    return (
      <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">
        No assigned equipment to configure for Carnet ATA.
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="h-9 w-28 px-2">Group</TableHead>
            <TableHead className="h-9 w-12 px-2 text-right">Qty</TableHead>
            <TableHead className="h-9 min-w-44 px-2">Equipment</TableHead>
            <TableHead className="h-9 min-w-56 px-2">ATA Description</TableHead>
            <TableHead className="h-9 w-44 px-2">URSPRUNGSLAND</TableHead>
            <TableHead className="h-9 w-32 px-2">
              Value for Carnet ATA
            </TableHead>
            <TableHead className="h-9 w-28 px-2">Weight</TableHead>
            <TableHead className="h-9 w-20 px-2">Unit</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ groupName, assignment }) => (
            <CarnetAtaTableRow
              key={assignment.id}
              groupName={groupName}
              assignment={assignment}
              onUpdateEquipment={onUpdateEquipment}
              onUpdateTemporaryEquipment={onUpdateTemporaryEquipment}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
